import { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Monitor, Pencil, Search, Smartphone, Tablet, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { DEVICE_PREVIEW_WIDTHS } from './editable/responsiveVisibility'
import { BuilderBlockPreviewFrame } from './BuilderBlockPreview'
import { collectBlockTypes, fetchRegistryTypes } from './normalizeRegistry'
import { normalizePage } from './normalizePage'
import { useBuilderPages } from './hooks/useBuilderPages'
import { useScrimDismiss } from '../shared/hooks/useScrimDismiss'
import { useEscapeClose } from '../shared/hooks/useEscapeClose'
import { notify } from '../../notices'

/**
 * Choisir le MODÈLE dont partira une nouvelle page, sur aperçu réel.
 *
 * Un modèle est une `builder_page` marquée `isTemplate` : éditable dans le
 * builder comme n'importe quelle page, mais jamais servie publiquement. Créer
 * une page à partir de lui, c'est le DUPLIQUER (`/api/builder/page/duplicate`) —
 * d'où la règle qui fait tout l'intérêt de la fonctionnalité :
 *
 *  - un bloc ORDINAIRE du modèle est COPIÉ : la page créée en est propriétaire,
 *    l'éditer ne touche ni le modèle ni les autres pages ;
 *  - un BLOC LIÉ (`SharedBlock`, posé via « Ajouter un bloc existant ») garde sa
 *    référence : le header et le footer d'un site restent ainsi COMMUNS à toutes
 *    les pages qui en descendent, tout en restant déplaçables et supprimables
 *    page par page.
 *
 * POURQUOI DU JSX ET PAS DU YAML (rituel config-first) : cet écran monte des
 * `rjBuild` INCONNUS à la volée, lus dans le registre à l'exécution — aucune
 * capacité reactive-json ne monte un build arbitraire. Et c'est du chrome
 * d'éditeur (pop-in, montage paresseux, bascule d'appareil), pas du contenu de
 * page. C'est l'argument déjà écrit en tête de `BuilderBlockPreview.jsx`, dont
 * on réutilise le composant plutôt que d'en écrire un second.
 *
 * Props :
 *  - siteId : le site pour lequel on crée. Il CLOISONNE la liste (les modèles
 *    d'un site ne sont proposés que là) et rattache la page créée.
 *  - directoryId : le répertoire du WIKI pour lequel on crée — exclusif de
 *    `siteId`. La liste devient celle des modèles du wiki (des `builder_page`
 *    rangées et marquées `isTemplate`), la page naît DANS ce répertoire par
 *    l'API decoupled, et l'écran ouvert ensuite est `/docs/page/<nid>`. Les
 *    aperçus, eux, sont les mêmes : une page rangée porte un slug (fabriqué au
 *    presave) et se lit par `/api/builder/page/<slug>` comme les autres.
 *  - defaultModelNid : le modèle par défaut du site, présélectionné. Un ENTIER,
 *    comparable au `nid` que sert /api/builder/pages — le champ, lui, sort en
 *    chaîne (même piège que `homepageNid` dans site.yaml). C'est une
 *    présélection, jamais un automatisme : l'éditeur voit toujours ce qu'il crée.
 *  - manage : la MÊME liste, pour GÉRER les templates et non créer une page
 *    (« Modifier les templates », engrenage de l'écran du site). Une carte y
 *    ouvre le template dans l'éditeur ; ni sélection, ni titre de page à
 *    saisir. À l'inverse, en création, la carte ne mène jamais à l'édition du
 *    template : deux gestes, deux portes — on ne modifie pas le point de départ
 *    de toutes les pages en voulant n'en créer qu'une.
 */
export const BuilderTemplatePicker = ({ onClose, siteId = null, directoryId = null, defaultModelNid = null, manage = false }) => {
  const scrim = useScrimDismiss(onClose)
  const navigate = useNavigate()
  const titleId = useId()
  const nameFieldId = useId()
  const { templates, loading, duplicatePage, createInDirectory } = useBuilderPages(siteId, directoryId)

  const [registry, setRegistry] = useState(null)
  // `{[slug]: bloc[]}` — les blocs de chaque modèle, pour l'aperçu.
  const [blocsBySlug, setBlocsBySlug] = useState({})
  const [device, setDevice] = useState('desktop')
  const [query, setQuery] = useState('')
  // Le slug du modèle choisi. Amorcé sur le modèle par défaut du site dès que
  // la liste arrive (le nid vient du site, le slug de la liste).
  const [selected, setSelected] = useState(null)
  const [title, setTitle] = useState('')
  const [creating, setCreating] = useState(false)

  useEscapeClose(onClose)

  // Le contenu de chaque modèle. Les modèles se comptent sur les doigts d'une
  // main : un aller-retour par modèle, en parallèle, reste moins coûteux qu'un
  // endpoint de plus à maintenir. `normalizePage` est le MÊME normaliseur que
  // le canevas — la forme decoupled est plate en lecture seule et imbriquée
  // sous `data` en écriture, et lui seul sait choisir.
  //
  // Les défs à monter viennent APRÈS : seules celles des blocs de ces modèles.
  // Cet écran vit hors du canevas (pas encore de page), il ne lit donc pas le
  // registre du contexte builder — il charge les siennes.
  useEffect(() => {
    if (!templates.length) {
      if (!loading) setRegistry([])
      return undefined
    }
    let cancelled = false
    Promise.all(templates.map(async (tpl) => {
      try {
        const res = await fetch(`/api/builder/page/${tpl.slug}`, { credentials: 'include' })
        if (!res.ok) return [tpl.slug, [], []]
        // Les types se lisent sur la réponse BRUTE : elle porte aussi celui de
        // l'unité qu'embarque un bloc lié.
        const payload = await res.json()
        return [tpl.slug, normalizePage(payload).blocs, collectBlockTypes(payload)]
      } catch {
        return [tpl.slug, [], []]
      }
    })).then(async (entries) => {
      const bySlug = Object.fromEntries(entries.map(([slug, blocs]) => [slug, blocs]))
      const defs = await fetchRegistryTypes(entries.flatMap(([, , types]) => types))
      if (cancelled) return
      setBlocsBySlug(bySlug)
      setRegistry(defs ?? [])
    })
    return () => { cancelled = true }
  }, [templates, loading])

  const registryByType = useMemo(
    () => Object.fromEntries((registry ?? []).map(e => [e.type, e])),
    [registry],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return templates
    return templates.filter(t =>
      (t.title || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
  }, [templates, query])

  // Présélection : le modèle par défaut du site s'il est dans la liste, sinon le
  // premier. On n'y touche plus dès que l'éditeur a choisi (`selected` non nul),
  // sinon un rechargement de liste écraserait son choix.
  useEffect(() => {
    if (selected || !templates.length) return
    const byDefault = defaultModelNid && templates.find(t => t.nid === defaultModelNid)
    setSelected((byDefault ?? templates[0]).slug)
  }, [templates, defaultModelNid, selected])

  const selectedTemplate = templates.find(t => t.slug === selected) ?? null

  const handleCreate = useCallback(async (e) => {
    e.preventDefault()
    const trimmed = title.trim()
    if (!trimmed || !selectedTemplate || creating) return
    setCreating(true)
    try {
      // WIKI : le clonage du modèle (résumé, catégorie, étiquettes, icône et
      // BLOCS) est fait par Drupal au presave, sur la seule foi de
      // `template_source` — même résultat que la duplication du builder, par le
      // chemin qui sait statuer sur le droit de créer DANS ce répertoire.
      if (directoryId) {
        const nid = await createInDirectory(trimmed, selectedTemplate.nid, false)
        navigate(`/docs/page/${nid}?edit=1`)
        return
      }
      const slug = await duplicatePage(selectedTemplate.slug, trimmed, siteId, false)
      // Pas de `onClose()` : la navigation démonte l'écran qui porte la pop-in.
      navigate(`/builder/${slug}?edit=1`)
    } catch (err) {
      notify({ type: 'error', message: err?.message || 'La création a échoué.' })
      setCreating(false)
    }
  }, [title, selectedTemplate, creating, duplicatePage, createInDirectory, siteId, directoryId, navigate])

  // Portail vers <body> + z-index au-dessus de tout le chrome du builder : la
  // pop-in couvre 99 % du viewport, or les aperçus contiennent du contenu qui
  // s'empile haut (une navbar de bloc en `z-[100]`) et qui traverserait une
  // modale rendue en place, au z-50 du DS.
  return createPortal(
    <div className="modal-scrim modal-scrim-full" {...scrim}>
      <div
        className="modal-panel modal-panel-full flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="flex min-w-0 items-center gap-3">
            <h2 id={titleId} className="truncate">
              {manage ? 'Modifier les templates' : <>Créer une page à partir d&apos;un template</>}
            </h2>
            <span className="text-sm font-normal text-slate-500">
              {filtered.length} template{filtered.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="relative">
              <span className="sr-only">Rechercher un template</span>
              <Search size={15} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher…"
                className="input pl-8"
              />
            </label>
            <DeviceToggle current={device} onChange={setDevice} />
            <button type="button" onClick={onClose} className="btn btn-secondary btn-sm" aria-label="Fermer">
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="modal-body flex-1">
          {loading || registry === null ? (
            <div className="flex justify-center py-16"><span className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState siteId={siteId} directoryId={directoryId} hasTemplates={templates.length > 0} />
          ) : (
            // Gestion : une LISTE de liens-boutons, pas un groupe de radios —
            // il n'y a rien à choisir, chaque carte ouvre son template.
            <div
              role={manage ? 'list' : 'radiogroup'}
              aria-label={manage ? 'Templates du site' : 'Templates disponibles'}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filtered.map((tpl, index) => (
                <TemplateCard
                  key={tpl.slug}
                  template={tpl}
                  index={index}
                  device={device}
                  manage={manage}
                  isDefault={tpl.nid === defaultModelNid}
                  selected={!manage && selected === tpl.slug}
                  blocs={blocsBySlug[tpl.slug]}
                  registryByType={registryByType}
                  onSelect={manage
                    ? () => {
                        // Refermer d'abord : sous un shell qui reste monté, le
                        // mode resterait posé et la pop-in rouvrirait au retour.
                        onClose()
                        navigate(directoryId ? `/docs/page/${tpl.nid}?edit=1` : `/builder/${tpl.slug}?edit=1`)
                      }
                    : () => setSelected(tpl.slug)}
                />
              ))}
            </div>
          )}
        </div>

        {manage ? (
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary ml-auto">Fermer</button>
          </div>
        ) : (
        <form className="modal-footer items-center" onSubmit={handleCreate}>
          <label htmlFor={nameFieldId} className="field-label ml-auto">Titre de la page</label>
          <input
            id={nameFieldId}
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Nouvelle page…"
            className="input w-64"
          />
          <button type="button" onClick={onClose} className="btn btn-secondary">Annuler</button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!title.trim() || !selectedTemplate || creating}
          >
            {creating ? 'Création…' : 'Créer la page'}
          </button>
        </form>
        )}
      </div>
    </div>,
    document.body,
  )
}

/**
 * Une carte : l'aperçu RÉEL du modèle (ses blocs empilés, montés avec leur
 * contenu) et son intitulé, le tout dans UN bouton :
 *
 *  - en création, c'est un radio : le cliquer SÉLECTIONNE le template. L'état
 *    choisi se lit autrement qu'à la seule couleur (RGAA 3.1) : anneau épais,
 *    pastille cochée sur l'aperçu, bandeau du titre teinté qui dit
 *    « Sélectionné ». Aucun accès à l'édition du template d'ici ;
 *  - en gestion (`manage`), il OUVRE le template dans l'éditeur, et le bandeau
 *    l'annonce (« Modifier »).
 *
 * L'aperçu est `inert` + `aria-hidden` (cf. `BuilderBlockPreviewFrame`) : le
 * nom accessible du contrôle vient du seul texte de la carte.
 */
const TemplateCard = ({ template, index, device, manage, isDefault, selected, blocs, registryByType, onSelect }) => {
  // Un bloc dont le type a disparu du catalogue n'a pas de `rjBuild` : on le
  // saute plutôt que de faire tomber l'aperçu. Le modèle reste choisissable —
  // c'est la duplication, côté serveur, qui fait foi, pas cette vignette.
  const stack = (blocs ?? [])
    .map((b, i) => ({ key: `${b.id}-${i}`, block: registryByType[b.type], data: b.data }))
    .filter(entry => entry.block?.rjBuild)
  const name = template.title || template.slug
  const cardState = selected
    ? 'ring-2 ring-iris-600 ring-offset-2'
    : 'ring-1 ring-transparent hover:ring-slate-300'

  return (
    <div
      role={manage ? 'listitem' : undefined}
      className={`card relative flex h-full flex-col overflow-hidden transition-shadow ${cardState}`}
    >
      <button
        type="button"
        role={manage ? undefined : 'radio'}
        aria-checked={manage ? undefined : selected}
        aria-label={manage ? `Modifier le template ${name}` : undefined}
        onClick={onSelect}
        className="flex grow flex-col text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-iris-500"
      >
        {blocs === undefined ? (
          <div className="flex h-[120px] items-center justify-center"><span className="spinner" /></div>
        ) : stack.length === 0 ? (
          <p className="flex h-[120px] items-center justify-center px-4 text-center text-xs text-slate-400">
            {manage ? 'Template vide.' : 'Template vide — la page créée le sera aussi.'}
          </p>
        ) : (
          <BuilderBlockPreviewFrame
            blocks={stack}
            index={index}
            renderWidth={DEVICE_PREVIEW_WIDTHS[device]}
            className="grow"
          />
        )}
        <div
          className={`flex shrink-0 items-start gap-2 border-t px-3 py-2.5 transition-colors ${
            selected ? 'border-iris-200 bg-iris-50' : 'border-slate-100'
          }`}
        >
          <div className="min-w-0 flex-1">
            <p
              className={`truncate text-sm font-semibold ${selected ? 'text-iris-800' : 'text-slate-800'}`}
              title={template.title}
            >
              {name}
            </p>
            {template.description && (
              <p className="line-clamp-2 text-xs text-slate-500">{template.description}</p>
            )}
          </div>
          {isDefault && <span className="tag tag-iris shrink-0">Par défaut</span>}
          {selected && (
            <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-iris-700">
              <Check size={14} aria-hidden="true" />
              Sélectionné
            </span>
          )}
          {manage && (
            <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-iris-700" aria-hidden="true">
              <Pencil size={14} />
              Modifier
            </span>
          )}
        </div>
      </button>
      {/* Pastille cochée SUR l'aperçu : repérable d'un coup d'œil dans une
          grille d'aperçus qui se ressemblent. Décorative — l'état est déjà
          porté par `aria-checked`. */}
      {selected && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-iris-600 text-white shadow-md"
        >
          <Check size={16} strokeWidth={3} />
        </span>
      )}
    </div>
  )
}

const EmptyState = ({ siteId, directoryId, hasTemplates }) => (
  <div className="py-16 text-center text-sm text-slate-500">
    {hasTemplates ? (
      <p>Aucun template ne correspond à cette recherche.</p>
    ) : directoryId ? (
      <p>
        Le wiki n&apos;a pas encore de template. Créez-en un avec le bouton
        <strong> + </strong> de la barre : c&apos;est une page comme une autre,
        simplement jamais servie — elle sert de point de départ.
      </p>
    ) : siteId ? (
      <p>
        Ce site n&apos;a pas encore de template. Créez-en un depuis l&apos;engrenage
        <strong> Templates et modèles </strong> de l&apos;écran du site, puis posez-y
        son header et son footer en « bloc existant » pour qu&apos;ils restent
        communs à toutes ses pages.
      </p>
    ) : (
      <p>
        Les templates appartiennent à un site : ouvrez l&apos;écran d&apos;un site pour
        y créer une page à partir de l&apos;un des siens.
      </p>
    )}
  </div>
)

/**
 * Les trois appareils d'aperçu — même vocabulaire et mêmes bandes de largeur
 * que le catalogue de blocs et que `hiddenOn`. La largeur choisie DIMENSIONNE
 * le conteneur de requêtes de l'aperçu : hors de lui, tout bloc retombe sur sa
 * version mobile.
 */
const DEVICES = [
  { id: 'mobile', Icon: Smartphone, label: 'Mobile' },
  { id: 'tablet', Icon: Tablet, label: 'Tablette' },
  { id: 'desktop', Icon: Monitor, label: 'Ordinateur' },
]

const DeviceToggle = ({ current, onChange }) => (
  <div className="flex items-center gap-1" role="group" aria-label="Largeur d'aperçu">
    {DEVICES.map(d => (
      <button
        key={d.id}
        type="button"
        onClick={() => onChange(d.id)}
        aria-pressed={current === d.id}
        aria-label={d.label}
        title={`Aperçu ${d.label.toLowerCase()} — ${DEVICE_PREVIEW_WIDTHS[d.id]} px`}
        className={current === d.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
      >
        <d.Icon size={15} aria-hidden="true" />
      </button>
    ))}
  </div>
)
